import React from 'react';

function SupportedModelsContent() {
  return (
    <>
      <p>RepoPrompt integrates with multiple AI providers through their APIs, offering access to a wide range of language models for your development needs.</p>
      
      <h3>Supported AI Providers</h3>
      
      <h4>OpenAI</h4>
      <ul>
        <li><strong>Available Models:</strong> GPT-4o, GPT-4o Mini, o1, o1-mini, o1-preview, o3-mini, o3-mini-low, o3-mini-high</li>
        <li><strong>API Setup:</strong> Requires an OpenAI API key from <a href="https://platform.openai.com/docs/overview" target="_blank" rel="noopener noreferrer">OpenAI Platform</a></li>
        <li><strong>Features:</strong> Most models support diff editing for precise code changes</li>
      </ul>
      
      <h4>Anthropic</h4>
      <ul>
        <li><strong>Available Models:</strong> Claude 3.5 Sonnet, Claude 3.5 Haiku, Claude 3.7 Sonnet, Claude 3.7 Sonnet Thinking, Claude 3.7 Sonnet Thinking Max</li>
        <li><strong>API Setup:</strong> Requires an Anthropic API key from <a href="https://console.anthropic.com/settings/keys" target="_blank" rel="noopener noreferrer">Anthropic Console</a></li>
        <li><strong>Features:</strong> Claude 3.5 and 3.7 models excel at precise code editing and understanding context</li>
      </ul>
      
      <h4>Gemini</h4>
      <ul>
        <li><strong>Available Models:</strong> Gemini 1.5 Pro, Gemini 1.5 Flash, Gemini 1.5 Flash 8b, Gemini 2.0 Flash, Gemini Flash Thinking, Gemini 2.5 Pro</li>
        <li><strong>API Setup:</strong> Requires a Gemini API key from <a href="https://aistudio.google.com" target="_blank" rel="noopener noreferrer">Google AI Studio</a></li>
        <li><strong>Features:</strong> Gemini 2.0 Flash and Gemini 2.5 Pro offer excellent performance for whole file editing</li>
      </ul>
      
      <h4>DeepSeek</h4>
      <ul>
        <li><strong>Available Models:</strong> DeepSeek V3, DeepSeek R1</li>
        <li><strong>API Setup:</strong> Requires a DeepSeek API key from <a href="https://platform.deepseek.com/api_keys" target="_blank" rel="noopener noreferrer">DeepSeek Platform</a></li>
        <li><strong>Features:</strong> Both models support diff editing with strong code understanding capabilities</li>
      </ul>
      
      <h4>GitHub</h4>
      <ul>
        <li><strong>Available Models:</strong> GitHub GPT-4o, GitHub GPT-4o Mini</li>
        <li><strong>API Setup:</strong> Authenticate with GitHub account</li>
        <li><strong>Features:</strong> Access to OpenAI models for GitHub users without requiring separate API keys</li>
      </ul>
      
      <h4>Azure OpenAI</h4>
      <ul>
        <li><strong>Available Models:</strong> Custom deployed models via Azure</li>
        <li><strong>API Setup:</strong> Requires Azure resource name, deployment ID, API version, and key</li>
        <li><strong>Features:</strong> Enterprise-grade deployment with custom resource management</li>
      </ul>
      
      <h4>OpenRouter</h4>
      <ul>
        <li><strong>Available Models:</strong> Access to models from various providers, including DeepSeek, Qwen, OpenAI, Anthropic, and Google</li>
        <li><strong>API Setup:</strong> Requires an OpenRouter API key</li>
        <li><strong>Features:</strong> Single API to access multiple providers, including options not directly supported</li>
      </ul>
      
      <h4>Local Models (Ollama)</h4>
      <ul>
        <li><strong>Available Models:</strong> Any model deployed through Ollama or other local services</li>
        <li><strong>API Setup:</strong> Configure URL (default: http://localhost:11434) and select available models</li>
        <li><strong>Features:</strong> Run models locally without sending data to external services</li>
      </ul>
      
      <h4>Custom Provider</h4>
      <ul>
        <li><strong>Features:</strong> Connect to any OpenAI-compatible API endpoint</li>
        <li><strong>API Setup:</strong> Configure base URL, API key, and other parameters</li>
        <li><strong>Use Cases:</strong> Self-hosted models or alternative API providers</li>
      </ul>
      
      <h3>Setting Up API Keys</h3>
      <p>To configure your API keys in RepoPrompt:</p>
      <ol>
        <li>Open RepoPrompt's settings</li>
        <li>Navigate to the "API Keys" tab</li>
        <li>Enter your API key for each service you want to use</li>
        <li>Click "Validate & Save" to securely store and verify your key</li>
      </ol>
      <p>API keys are stored securely in your system's keychain and never shared with external services beyond the respective AI provider.</p>
      
      <h3>Model Capabilities</h3>
      <p>Different models have different capabilities when it comes to code editing:</p>
      <ul>
        <li><strong>Diff Editing Capable:</strong> Models like Claude 3.5/3.7 Sonnet, DeepSeek V3, GPT-4o, and o3-mini-high can provide precise diff edits that only modify the necessary parts of a file.</li>
        <li><strong>Whole File Editing:</strong> All models can rewrite entire files, with models like Gemini Flash 2.0 excelling at this task for smaller files.</li>
      </ul>
      <p>You can customize model capabilities in the Model Overrides settings, enabling or disabling features like diff editing and streaming for specific models.</p>
      
      <h3>Pro Edit Settings</h3>
      <p>RepoPrompt's Pro Mode intelligently distributes editing tasks across different models:</p>
      <ul>
        <li><strong>Planning:</strong> The primary model you select handles overall planning and complex decisions</li>
        <li><strong>Simple Edits:</strong> Typically handled by faster, more cost-effective models like Gemini Flash 2.0</li>
        <li><strong>Complex Edits:</strong> Assigned to powerful models like Claude 3.7 Sonnet for difficult changes</li>
        <li><strong>Large Files:</strong> Processed by models with high context windows and diff capabilities</li>
      </ul>
      <p>The application automatically adjusts editing strategies based on file size and complexity.</p>
    </>
  );
}

export default SupportedModelsContent;